import { Mail, Inbox as InboxIcon, MessageSquare } from "lucide-react";
import { normalizeToProviderConfigKey } from "@/lib/constants/email-providers";

export interface ProviderConfig {
  id: string;
  providerKey: string;
  displayName: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  badgeColor: string;
  description: string;
  scopes: string[];
}

export const providers: Record<string, ProviderConfig> = {
  "google-mail": {
    id: "google-mail",
    providerKey: "google-mail",
    displayName: "Gmail",
    icon: Mail,
    color: "text-red-500",
    badgeColor: "bg-red-500/10 text-red-500 dark:bg-red-400/10 dark:text-red-400",
    description: "Connect your Gmail account to sync emails and manage your inbox",
    scopes: [
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/userinfo.email",
    ],
  },
  "outlook-oauth": {
    id: "outlook-oauth",
    providerKey: "outlook-oauth",
    displayName: "Microsoft Outlook",
    icon: InboxIcon,
    color: "text-blue-500",
    badgeColor: "bg-blue-500/10 text-blue-500 dark:bg-blue-400/10 dark:text-blue-400",
    description: "Connect your Outlook account to access emails and calendar",
    scopes: [
      "https://graph.microsoft.com/mail.read",
      "https://graph.microsoft.com/mail.readwrite",
      "https://graph.microsoft.com/mailboxsettings.read",
      "https://graph.microsoft.com/user.read",
    ],
  },
  "slack-oauth": {
    id: "slack-oauth",
    providerKey: "slack-oauth",
    displayName: "Slack",
    icon: MessageSquare,
    color: "text-purple-500",
    badgeColor: "bg-purple-500/10 text-purple-500 dark:bg-purple-400/10 dark:text-purple-400",
    description: "Connect your Slack workspace to search messages and files, and sync channels",
    // search:read powers Meridian's search_slack_messages / search_slack_files chat tools
    // (Slack's search.messages / search.files require a per-user token, not a bot token).
    scopes: [
      "channels:read",
      "channels:history",
      "chat:write",
      "users:read",
      "users:read.email",
      "search:read",
    ],
  },
} as const;

// Helper functions
export function getProviderConfig(providerKey: string): ProviderConfig | null {
  // Direct match first, then normalize any alias / legacy template slug
  // (e.g. "outlook" → "outlook-oauth") through the canonical resolver.
  return providers[providerKey] ?? providers[normalizeToProviderConfigKey(providerKey)] ?? null;
}

export function getProviderDisplayName(providerKey: string): string {
  const config = getProviderConfig(providerKey);
  return config?.displayName || providerKey;
}

export function getProviderIcon(providerKey: string): React.ComponentType<{ className?: string }> {
  const config = getProviderConfig(providerKey);
  return config?.icon || Mail;
}

export function getProviderColor(providerKey: string): string {
  const config = getProviderConfig(providerKey);
  return config?.color || "text-gray-500";
}

export function getProviderBadgeColor(providerKey: string): string {
  const config = getProviderConfig(providerKey);
  return config?.badgeColor || "bg-card-500/10 text-gray-500";
}

export function getProviderDescription(providerKey: string): string {
  const config = getProviderConfig(providerKey);
  return config?.description || "Unknown provider";
}

export function getProviderScopes(providerKey: string): string[] {
  const config = getProviderConfig(providerKey);
  return config?.scopes || [];
}

export function getProviderKey(providerKey: string): string {
  const config = getProviderConfig(providerKey);
  return config?.providerKey || providerKey;
}

// Type for provider keys
export type ProviderKey = keyof typeof providers;

// Export all provider keys for type safety
export const providerKeys = Object.keys(providers) as ProviderKey[];
